// Premium "Feature Disabled" screen — iter177.
//
// Designed to NEVER feel like an error page. Instead it explains the state
// calmly, lists modules the tenant DOES have, and offers one-click shortcuts.
// Sidebar + top-nav remain fully visible because this is rendered inside
// the standard `<Layout>` shell.
import { Link } from 'react-router-dom';
import { Lock, ArrowRight, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { listEnabledLandings, pickLandingPath } from '../config/modules';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const FEATURE_LABELS = {
  dashboard: 'Dashboard',
  pnl_live: 'Live P&L',
  pnl_finalize_compare: 'P&L Finalize / Compare',
  cost_insights: 'Cost Insights',
  crm: 'Customers (CRM)',
  customer_feedback: 'Customer Experience',
  custom_feedback_forms: 'Customer Pulse Index',
  expansion_intelligence: 'Expansion Intelligence',
  checklist: 'Checklist',
  inventory_module: 'Inventory',
  recipe_module: 'Recipes',
  petty_cash: 'Petty Cash',
  communication: 'Communication',
  marketing: 'Marketing',
  user_management: 'User Management',
  outlet_management: 'Outlets',
  reports: 'Reports',
  pricing_module: 'Pricing',
  production_planner: 'Production',
  kitchen_closing: 'Kitchen Closing',
  bar_closing: 'Bar Closing',
};

const friendlyLabel = (key) =>
  FEATURE_LABELS[key] ||
  (key || '')
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export default function FeatureDisabledScreen({ feature }) {
  const { user, tenantFeatures } = useAuth();
  const label = friendlyLabel(feature);
  const enabled = listEnabledLandings(user?.role, tenantFeatures, user);
  const primaryLanding = pickLandingPath(user, tenantFeatures);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-[#F7F9F6] via-[#FAFBF8] to-[#F0F4F0]"
         data-testid="feature-disabled-screen">
      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-10 sm:py-14">

        {/* ─── Explanation card ─────────────────────────────────── */}
        <Card className="border-[#E2E8DF] mb-7 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-[var(--color-primary)] via-[#52A57B] to-[var(--color-primary)]" />
          <CardContent className="p-7 sm:p-9">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#EEF4EF] text-[color:var(--color-primary)] flex items-center justify-center shrink-0">
                <Lock className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold mb-1.5">
                  Module not enabled for this property
                </div>
                <h1 className="text-[22px] sm:text-[26px] font-semibold text-[#1A2421] tracking-tight">
                  <span data-testid="feature-disabled-label">{label}</span> isn&apos;t included in your current setup.
                </h1>
                <p className="text-[13.5px] text-[#5C6B64] leading-relaxed mt-2 max-w-2xl">
                  Your administrator can enable this module from the platform's Super Admin
                  control panel. Until then, every other module you DO have access to
                  remains fully available below.
                </p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {primaryLanding && (
                    <Link to={primaryLanding} data-testid="feature-disabled-go-home">
                      <Button className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white h-9 text-[12.5px]">
                        Take me home
                        <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                      </Button>
                    </Link>
                  )}
                  <a href="mailto:support@example.com?subject=Enable%20module%3A%20"
                     data-testid="feature-disabled-contact">
                    <Button variant="outline" className="h-9 text-[12.5px]">
                      <Mail className="w-3.5 h-3.5 mr-1.5" /> Contact administrator
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ─── Available modules grid ───────────────────────────── */}
        {enabled.length > 0 && (
          <>
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B64] font-semibold">
                  Your available modules
                </div>
                <h2 className="text-[16px] font-semibold text-[#1A2421] mt-1">
                  {enabled.length} module{enabled.length === 1 ? '' : 's'} active
                </h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3"
                 data-testid="feature-disabled-modules">
              {enabled.map((m) => (
                <Link
                  key={m.path}
                  to={m.path}
                  className="group block"
                  data-testid={`feature-disabled-shortcut-${m.feature || 'open'}`}
                >
                  <Card className="border-[#E2E8DF] hover:border-[color:var(--color-primary)] hover:shadow-md transition-all duration-200">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-[13.5px] font-medium text-[#1A2421] truncate">
                          {m.label}
                        </div>
                        <div className="text-[10.5px] text-[#5C6B64] mt-0.5">
                          Open module
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#9AAA9F] group-hover:text-[color:var(--color-primary)] group-hover:translate-x-0.5 transition-all shrink-0" />
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
